import { useState, useCallback } from 'react';
import { createContainer } from 'unstated-next';
import { useDataProvider, useGetOne } from 'react-admin';
import {
  BoardTypeDto,
  BoardTypeCreateDto,
  ContractorResourceTypeEnum,
} from '@vatos-pas/common';

export interface BoardFormValues {
  name: string;
  shortName: string;
  externalId: string;
  payableResourceTypes: ContractorResourceTypeEnum[];
  laborOnly: boolean;
}

/**
 * Adapter between the data layer and board components — components stay
 * data-layer agnostic so the underlying client (react-admin, react-query,
 * Apollo, fetch) can be swapped without touching them.
 *
 * `useGetOne` provides SWR semantics via the react-admin Redux cache: cached
 * data returns immediately, refetch happens in the background, and the loading
 * spinner only shows on first load when no cached data exists.
 */
function useBoard() {
  const dataProvider = useDataProvider();

  const [boardId, setBoardId] = useState<string>('');
  const {
    data: selectedBoard,
    loading: boardLoading,
    loaded: boardLoaded,
    error: boardError,
  } = useGetOne<BoardTypeDto>('board-type', boardId, { enabled: !!boardId });

  const isLoading = !!boardId && !selectedBoard && boardLoading;

  const [formLoading, setFormLoading] = useState(false);

  const [existingBoards, setExistingBoards] = useState<BoardTypeDto[]>([]);

  const loadBoardById = useCallback((id: string): void => {
    setBoardId(id);
  }, []);

  const createBoard = useCallback(
    async (data: BoardTypeCreateDto): Promise<BoardTypeDto> => {
      const result = await dataProvider.create<BoardTypeDto>('board-type', {
        data,
      });
      setBoardId(result.data.id);
      return result.data;
    },
    [dataProvider],
  );

  const updateBoard = useCallback(
    async (
      id: string,
      data: Partial<BoardTypeDto>,
    ): Promise<BoardTypeDto> => {
      const result = await dataProvider.update<BoardTypeDto>('board-type', {
        id,
        data: { ...selectedBoard, ...data },
        previousData: selectedBoard || { id },
      });
      return result.data;
    },
    [dataProvider, selectedBoard],
  );

  const loadBoardTypesForValidation = useCallback(async (): Promise<
    BoardTypeDto[]
  > => {
    const result = await dataProvider.getList<BoardTypeDto>('board-type', {
      filter: {},
      pagination: { page: 1, perPage: 999 },
      sort: { field: 'name', order: 'ASC' },
    });
    setExistingBoards(result.data);
    return result.data;
  }, [dataProvider]);

  return {
    selectedBoard,
    isLoading,
    boardLoaded,
    boardError,
    formLoading,
    setFormLoading,
    loadBoardById,
    createBoard,
    updateBoard,
    existingBoards,
    loadBoardTypesForValidation,
  };
}

export const BoardContainer = createContainer(useBoard);
